import { Channel, Order } from "@/services/backend/vbs/orders/types/order";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import t from "@/lang/lang";
import { useEffect, useState } from "react";
import {
  PutRequest,
  useVbsOrderPutMutation,
} from "@/services/backend/vbs/orders/api/order-api";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { cn } from "@/shared/lib/utils";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { DatePicker } from "@/shared/components/date-picker";
import { useVbsOfferListQuery } from "@/services/backend/vbs/offers/api/offer-api";
import { useToast } from "@/shared/hooks/use-toast";
import { Input } from "@/shared/components/ui/input";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { NullUUID } from "@/shared/lib/utilities/uuid";
import { useRefEffect } from "@/shared/hooks/use-ref-effect";

interface Props {
  order: Order;
}

export function OrderCard({ order }: Props) {
  const [request, setRequest] = useState<PutRequest>({
    ...order,
  });
  const mutation = useVbsOrderPutMutation();
  useDebouncedMutation(request, setRequest, mutation, { ...order });

  useRefEffect(order.offerId, () => {
    setRequest(order);
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex">
          <span>{t("Auftragseingang")}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-2">
        <OfferSelect
          offerId={request.offerId}
          onOfferIdChange={(offerId) => setRequest({ ...request, offerId })}
          processId={order.processId}
        />
        <Datepicker
          date={request.date}
          onDateChange={(date) => setRequest({ ...request, date })}
        />
        <ChannelSelect
          channel={request.channel}
          onChannelChange={(channel) => setRequest({ ...request, channel })}
        />
        <CustomerReference
          customerReference={request.customerReference}
          onCustomerReferenceChange={(customerReference) =>
            setRequest({ ...request, customerReference })
          }
        />
        <InternalComment
          internalComment={request.internalComment}
          onInternalCommentChange={(internalComment) =>
            setRequest({ ...request, internalComment })
          }
          className="md:col-span-2"
        />
      </CardContent>
    </Card>
  );
}

export function OrderCardSkeleton() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex">
          <span>{t("Auftragseingang")}</span>
        </CardTitle>
      </CardHeader>
    </Card>
  );
}

interface OfferDropdownProps {
  offerId: string | null;
  onOfferIdChange: (offerId: string | null) => void;
  processId: string | null;
}

function OfferSelect({
  offerId,
  onOfferIdChange,
  processId,
}: OfferDropdownProps) {
  const {
    data: list,
    isLoading,
    error,
  } = useVbsOfferListQuery({
    process: {
      active: processId !== null,
      values: [processId ?? NullUUID],
    },
    immutable: {
      active: true,
      values: [true],
    },
  });
  const { toast } = useToast();

  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: "Error loading offers",
        variant: "destructive",
      });
    }
  }, [error, toast]);

  if (isLoading) {
    return (
      <div className="grid animate-pulse content-start gap-1.5">
        <Label>{t("Zu Angebot")}</Label>
        <Select disabled>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t("Angebot auswählen")} />
          </SelectTrigger>
        </Select>
      </div>
    );
  }

  return (
    <div className="grid content-start gap-1.5">
      <Label>{t("Zu Angebot")}</Label>
      <Select
        value={offerId ?? undefined}
        onValueChange={(v) => {
          if (v === "00000000-0000-0000-0000-000000000000") {
            onOfferIdChange(null);
            return;
          }
          onOfferIdChange(v);
        }}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={t("Angebot auswählen")} />
        </SelectTrigger>
        <SelectContent>
          {list?.items.map((offer) => (
            <SelectItem value={offer.id} key={offer.id}>
              <DocumentNumberById documentId={offer.id} />
            </SelectItem>
          ))}
          <SelectItem value="00000000-0000-0000-0000-000000000000">
            {t("Kein Angebot")}
          </SelectItem>
        </SelectContent>
      </Select>
      <div className="text-muted-foreground text-xs">
        {t(
          "Die Auswahl eines Angebots überschreibt die Leistungen des Auftragseingangs mit denen des Angebots.",
        )}
      </div>
    </div>
  );
}

interface DatepickerProps {
  date: string | null;
  onDateChange: (date: string | null) => void;
}

function Datepicker({ date, onDateChange }: DatepickerProps) {
  const formattedDate = date ? new Date(date) : undefined;
  const onFormattedDateChange = (d: Date | undefined) => {
    if (!d) {
      onDateChange(null);
      return;
    }

    // We want to add the current time to the date
    const dateTime = new Date();
    dateTime.setUTCFullYear(d.getUTCFullYear(), d.getMonth(), d.getDate());
    onDateChange(dateTime.toISOString());
  };

  return (
    <div className="grid content-start gap-1.5">
      <Label>{t("Eingangsdatum")}</Label>
      <DatePicker date={formattedDate} onDateChange={onFormattedDateChange} />
    </div>
  );
}

interface ChannelSelectProps {
  channel: string | null;
  onChannelChange: (channel: string | null) => void;
}

function ChannelSelect({ channel, onChannelChange }: ChannelSelectProps) {
  const channels = Object.entries(Channel).map((chan) => chan[1]);

  return (
    <div className="grid content-start gap-1.5">
      <Label>{t("Kanal")}</Label>
      <Select value={channel ?? undefined} onValueChange={onChannelChange}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder={t("Kanal auswählen")} />
        </SelectTrigger>
        <SelectContent>
          {channels.map((chan) => (
            <SelectItem value={chan} key={chan}>
              {t(chan)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

interface CustomerReferenceProps {
  customerReference: string | null;
  onCustomerReferenceChange: (comment: string | null) => void;
  className?: string;
}

function CustomerReference({
  customerReference,
  onCustomerReferenceChange,
  className = "",
}: CustomerReferenceProps) {
  return (
    <div className={cn("grid content-start gap-1.5", className)}>
      <Label>{t("Referenz des Kunden")}</Label>
      <Input
        value={customerReference ?? ""}
        onChange={(e) => onCustomerReferenceChange(e.target.value || null)}
      />
      <div className="text-muted-foreground text-xs">
        {t("z.B. Bestellnummer, Auftragsnummer")}
      </div>
    </div>
  );
}

interface InternalCommentProps {
  internalComment: string | null;
  onInternalCommentChange: (internalComment: string | null) => void;
  className?: string;
}

function InternalComment({
  internalComment,
  onInternalCommentChange,
  className = "",
}: InternalCommentProps) {
  return (
    <div className={cn("grid content-start gap-1.5", className)}>
      <Label>{t("Interner Kommentar")}</Label>
      <Textarea
        value={internalComment ?? ""}
        onChange={(e) => onInternalCommentChange(e.target.value || null)}
      />
    </div>
  );
}
